<template>
  <div class="side-box__content">
    <div v-if="errors.endpoint" class="text-danger">
      {{ errors.endpoint }}
    </div>
    <div class="side-box__row d-flex align-items-center mt-3">
      <span class="side-box__content__label">
        <div class="side-box__content__label__withTooltip">
          <InfoQuestionMark :info-tooltip-text="'tooltip_novyEndpoint_nazevEndpointu'" />
          {{ $t("nazevEndpointu") }}
        </div>
      </span>
      <div
        class="side-box__content__input custom-control__custom-input ml-3"
        :class="{ 'has-error': errors.endpoint && !endpoint.custDestName }"
      >
        <span v-if="!endpoint.custDestName" class="placeholder">{{ $t("zadejteNazevEndpointu") }}</span>
        <input
          v-model="endpoint.custDestName"
          type="text"
          class="w-100"
          maxlength="60"
          minlength="1"
          placeholder=""
          autofocus
          @click="$emit('somethingClicked')"
        />
      </div>
    </div>
    <FormInput
      :value="endpoint.custDestDescription || ''"
      @input="endpoint.custDestDescription = $event"
      :label="$t('volitelnyPopisEndpointu')"
      side-box
    />
    <div v-if="!fixedProtocol" class="side-box__row d-flex align-items-center mt-3">
      <span class="side-box__content__label">{{ $t("PROTOKOL_LABEL") }}</span>
      <div class="side-box__content__input custom-control__custom-input ml-3" @click="$emit('somethingClicked')">
        <div
          class="side-box__content__input custom-control__select__selected"
          :class="{ opened: protocolsOpened }"
          @click="protocolsOpened = !protocolsOpened"
        >
          <span v-if="!selectedProtocol.value" class="gray-text">{{ $t("PROTOKOL_PLACEHOLDER") }}</span>
          <div v-else class="d-flex align-items-center justify-content-between w-100">
            <span class="font-weight-light">
              {{ selectedProtocol.label }}
            </span>
          </div>
        </div>
        <div class="custom-control__select__options" :class="{ opened: protocolsOpened }">
          <div
            v-for="protocol in protocolsChoices"
            :key="`protocolChoice${protocol.value}`"
            @click="handleProtocolChanged(protocol)"
          >
            <div class="custom-control__select__option d-flex align-items-center">
              <span class="font-weight-light">{{ protocol.label }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="selectedProtocolFields.includes('url')" class="side-box__row d-flex align-items-center mt-3">
      <span class="side-box__content__label">
        <div class="side-box__content__label__withTooltip">
          <InfoQuestionMark :info-tooltip-text="'tooltip_novyEndpoint_url'" />
          {{ $t("url") }}
        </div>
      </span>
      <div
        class="side-box__content__input custom-control__custom-input ml-3"
        :class="{
          'has-error': errors.endpoint && !endpoint.custDestParameters.url
        }"
      >
        <span v-if="!endpoint.custDestParameters.url" class="placeholder">{{ $t("https://") }}</span>
        <input
          v-model="endpoint.custDestParameters.url"
          type="text"
          class="w-100"
          maxlength="1000"
          placeholder=""
          autofocus
          @click="$emit('somethingClicked')"
        />
      </div>
    </div>
    <div v-if="selectedProtocolFields.includes('auth')" class="side-box__row d-flex align-items-center mt-3">
      <span class="side-box__content__label">
        <div class="side-box__content__label__withTooltip">
          <InfoQuestionMark :info-tooltip-text="'tooltip_novyEndpoint_autorizacniKlic'" />
          {{ $t("autorizacniKlic") }}
        </div>
      </span>
      <div class="side-box__content__input custom-control__custom-input ml-3">
        <span v-if="!endpoint.custDestParameters.auth" class="placeholder" />
        <input
          v-model="endpoint.custDestParameters.auth"
          type="text"
          class="w-100"
          maxlength="1000"
          placeholder=""
          value=""
          autofocus
          @click="$emit('somethingClicked')"
        />
      </div>
    </div>

    <EndpointHeadersField
      v-if="selectedProtocolFields.includes('headers')"
      :headers="endpoint.custDestParameters.headers"
      @update="endpoint.custDestParameters.headers = $event"
      @changed="$emit('somethingClicked')"
    />

    <div v-if="selectedProtocolFields.includes('transformationId')" class="side-box__content">
      <TransformFunctionField
        :initial-transform-id="endpoint.transformationId"
        :opened-boxes="opened"
        :sub-type="selectedProtocol.value"
        type="OUT"
        @change="handleTransformChanged"
        @close-box="$emit('closeBox', $event)"
        @open-box="$emit('openBox', $event)"
      />
    </div>

    <div
      v-if="selectedProtocolFields.includes('custDestEnabled')"
      class="side-box__row mt-4 pb-4 d-flex align-items-center"
    >
      <span class="side-box__content__label">
        <div class="side-box__content__label__withTooltip side-box__content__label__withTooltip">
          <InfoQuestionMark
            :info-tooltip-text="'tooltip_novyEndpoint_chciEndpointZapnout'"
            class="atom-info--questionMark--special"
          />
          <span class="hide-and-seek">Tooltip</span>
        </div>
      </span>
      <div
        class="w-100 side-box__content__input d-flex align-items-center pointer"
        @click="endpoint.custDestEnabled = !endpoint.custDestEnabled"
      >
        <div class="ml-sm-3 custom-control custom-control--checkbox" :class="{ checked: endpoint.custDestEnabled }">
          <input type="checkbox" class="custom-control-input" />
        </div>
        {{ $t("chciEndpointZapnout") }}
      </div>
    </div>

    <div v-if="selectedProtocolFields.includes('subtopic')">
      <div class="side-box__row d-flex align-items-center mt-3">
        <span class="side-box__content__label">
          <span class="side-box__content__label__withTooltip side-box__content__label__withTooltip">
            <InfoQuestionMark info-tooltip-text="ENDPOINT_SUBTOPIC_TOOLTIP" />
            {{ $t("ENDPOINT_SUBTOPIC_LABEL") }}
          </span>
        </span>
        <div
          :class="{ 'has-error': (errors.endpoint && !endpoint.custDestParameters.address) || !isSubtopicValid }"
          class="side-box__content__input custom-control__custom-input ml-3"
        >
          <span v-if="!endpointSubtopic" class="placeholder">
            {{ $t("ENDPOINT_SUBTOPIC_PLACEHOLDER") }}
          </span>
          <input
            :value="endpointSubtopic"
            autofocus
            class="w-100"
            maxlength="60"
            type="text"
            @click="$emit('somethingClicked')"
            @input="handleSubtopicChanged($event.target.value)"
          />
        </div>
      </div>
      <div v-if="!isSubtopicValid" class="text-danger w-50" style="margin-left: 160px">
        {{ $t("SUBTOPIC_INVALID") }}
      </div>
    </div>
    <div v-if="selectedProtocol.value && !['HTTP', 'MQTT'].includes(selectedProtocol.value)">
      <!-- TODO better CSS definition -->
      <p style="padding-left: calc(140px + 1rem); padding-top: 1rem">
        {{ $t("CONTACT_SALES_FOR_ACTIVATION") }}
      </p>
    </div>
  </div>
</template>

<script>
import TransformFunctionField from "@/components/inputs/specific/TransformFunctionField.vue"
import InfoQuestionMark from "@/components/info/infoQuestionMark.vue"
import FormInput from "@/components/forms/FormInput.vue"
import EndpointHeadersField from "@/components/modals/endpoints/EndpointHeadersField.vue"

export default {
  name: "NewEndpointFormFields",
  components: { FormInput, InfoQuestionMark, TransformFunctionField, EndpointHeadersField },
  props: {
    endpoint: {
      type: Object,
      required: true
    },
    errors: {
      type: Object,
      required: true
    },
    fixedProtocol: {
      type: String,
      default: ""
    },
    opened: {
      type: Array,
      required: true
    }
  },
  data: () => ({
    protocolsChoices: [
      { value: "HTTP", label: "REST" },
      { value: "MQTT", label: "MQTT" },
      { value: "EMAIL", label: "E-mail" },
      { value: "SMS", label: "SMS" },
      { value: "Slack", label: "Slack" },
      { value: "Teams", label: "Teams" }
    ],
    protocolFieldMap: {
      HTTP: ["url", "auth", "headers", "transformationId", "custDestEnabled"],
      MQTT: ["transformationId", "custDestEnabled", "subtopic"]
    },
    protocolsOpened: false,
    selectedProtocol: {}
  }),
  watch: {
    opened() {
      if (this.fixedProtocol) {
        this.selectedProtocol = this.protocolsChoices.find((pc) => pc.value === this.fixedProtocol)
      } else {
        this.selectedProtocol = {}
      }
    }
  },
  computed: {
    isSubtopicValid() {
      if (!this.endpointSubtopic) {
        return true
      }
      const pattern = /^[a-zA-Z0-9_-]+$/
      const isValid = pattern.test(this.endpointSubtopic)
      this.$emit("subtopicValid", isValid)
      return isValid
    },
    endpointSubtopic() {
      if (this.endpoint.custDestParameters?.address) {
        const addressParts = this.endpoint.custDestParameters?.address.split(".")
        // TODO use .at(-1) instead of slice when ES2022 can be used
        return addressParts.pop() || ""
      }

      return ""
    },
    selectedProtocolFields() {
      return this.protocolFieldMap[this.selectedProtocol.value] || []
    }
  },
  created() {
    if (this.fixedProtocol) {
      this.selectedProtocol = this.protocolsChoices.find((pc) => pc.value === this.fixedProtocol)
    }
  },
  methods: {
    handleProtocolChanged(protocolChoice) {
      this.selectedProtocol = protocolChoice
      this.protocolsOpened = false
      this.endpoint.protocol = protocolChoice.value // do API to nepošlu, ale potřebuju to vědět pro výběr endpointu

      if (protocolChoice.value === "HTTP") {
        this.endpoint.custDestParameters = {
          url: "",
          auth: "",
          headers: {
            "Content-Type": "application/json"
          }
        }
      }

      if (protocolChoice.value === "MQTT") {
        this.endpoint.custDestParameters = {
          address: ""
        }
      }
    },
    handleSubtopicChanged(value) {
      if (!value) {
        this.endpoint.custDestParameters.address = null
      } else {
        this.endpoint.custDestParameters.address = `${this.$route.params.customerId}.${this.$route.params.projectId}.gate.${value}`
      }
    },
    handleTransformChanged(transform) {
      this.endpoint.transformationId = transform.transformationId
    },
    resetProtocol() {
      this.selectedProtocol = {}
    }
  }
}
</script>
